import { useCallback, useEffect, useRef, useState } from 'react';
import type { TaskCampaignFilters } from '../../models/Task/campaignFilters';
import type { ReportRequest } from '../../store/reducers/ReportSlice';

interface CampaignJournalRequest {
  readonly PageIndex: number;
  readonly 'Filter.FormTemplateIds'?: string[];
  readonly 'Filter.FormBatchIds'?: string[];
  readonly 'Filter.DispensarizationBatchIds'?: string[];
}

interface Props<T> {
  readonly types: readonly string[];
  readonly callRequest: T;
  readonly onCallRequest: (request: T) => void;
  readonly callRequestReport: ReportRequest;
  readonly onCallRequestReport: (request: ReportRequest) => void;
}

export const useJournalCampaignFilter = <T extends CampaignJournalRequest>({
  types,
  callRequest,
  onCallRequest,
  callRequestReport,
  onCallRequestReport,
}: Props<T>) => {
  const [draft, setDraft] = useState<TaskCampaignFilters>();
  const draftRef = useRef<TaskCampaignFilters>();
  const openRef = useRef(false);
  useEffect(() => {
    draftRef.current = undefined;
    setDraft(undefined);
  }, [
    types,
    callRequest['Filter.FormBatchIds'],
    callRequest['Filter.DispensarizationBatchIds'],
  ]);

  const apply = useCallback(
    (filters: TaskCampaignFilters) => {
      onCallRequest({
        ...callRequest,
        PageIndex: 0,
        'Filter.FormBatchIds': filters.FormBatchIds,
        'Filter.DispensarizationBatchIds': filters.DispensarizationBatchIds,
      });
      onCallRequestReport({ ...callRequestReport, ...filters });
    },
    [callRequest, onCallRequest, callRequestReport, onCallRequestReport],
  );

  const onChange = (filters: TaskCampaignFilters) => {
    if (openRef.current) {
      draftRef.current = filters;
      setDraft(filters);
    } else {
      apply(filters);
    }
  };
  const onOpenChange = (open: boolean) => {
    openRef.current = open;
    if (!open && draftRef.current) {
      apply(draftRef.current);
      draftRef.current = undefined;
      setDraft(undefined);
    }
  };

  return {
    types,
    value: draft ?? {
      FormBatchIds: callRequest['Filter.FormBatchIds'],
      DispensarizationBatchIds: callRequest['Filter.DispensarizationBatchIds'],
    },
    onChange,
    onOpenChange,
  };
};
